import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Wrapper from "../../components/wrapper/Wrapper";
import {
    FiPlus,
    FiEdit,
    FiTrash2,
    FiLink,
    FiExternalLink,
    FiFilter,
} from "react-icons/fi";
import {
    getAIFeedingsAPI,
    createAIFeedingAPI,
    updateAIFeedingAPI,
    deleteAIFeedingAPI,
} from "../../API/AIFeedingAPI";
import ButtonLoader from "../../components/button/ButtonLoader";
import Loader from "../../components/Loader/Loader";

const AIFeeding = () => {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [filterPlatform, setFilterPlatform] = useState("");
    const [filterCategory, setFilterCategory] = useState("");

    const [formData, setFormData] = useState({
        category: "",
        platform: "",
        url: "",
        title: "",
        isActive: true,
    });

    const { data, isLoading, error } = useQuery({
        queryKey: ["aiFeedings", filterPlatform, filterCategory],
        queryFn: () => getAIFeedingsAPI({ platform: filterPlatform, category: filterCategory }),
    });

    const createMutation = useMutation({
        mutationFn: createAIFeedingAPI,
        onSuccess: () => {
            queryClient.invalidateQueries(["aiFeedings"]);
            setIsModalOpen(false);
            resetForm();
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updateAIFeedingAPI(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["aiFeedings"]);
            setIsModalOpen(false);
            resetForm();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteAIFeedingAPI,
        onSuccess: () => {
            queryClient.invalidateQueries(["aiFeedings"]);
        },
    });

    const resetForm = () => {
        setFormData({
            category: "",
            platform: "",
            url: "",
            title: "",
            isActive: true,
        });
        setEditingLink(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingLink) {
            updateMutation.mutate({ id: editingLink._id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const handleEdit = (link) => {
        setEditingLink(link);
        setFormData({
            category: link.category,
            platform: link.platform,
            url: link.url,
            title: link.title || "",
            isActive: link.isActive,
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this link?")) {
            deleteMutation.mutate(id);
        }
    };

    const links = data?.data?.data || [];
    const uniqueCategories = [...new Set(links.map(l => l.category))];
    const uniquePlatforms = [...new Set(links.map(l => l.platform))];

    return (
        <Wrapper>
            <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">AI Feeding</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Manage reference links and examples for AI instructions
                    </p>
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setIsModalOpen(true);
                    }}
                    className="bg-[--primary] hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors w-full md:w-auto justify-center"
                >
                    <FiPlus className="w-4 h-4" />
                    Add New Link
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2 text-gray-500">
                    <FiFilter />
                    <span className="text-sm font-medium">Filter by:</span>
                </div>
                <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                >
                    <option value="">All Categories</option>
                    {uniqueCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
                <select
                    value={filterPlatform}
                    onChange={(e) => setFilterPlatform(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none"
                >
                    <option value="">All Platforms</option>
                    {uniquePlatforms.map(plat => (
                        <option key={plat} value={plat}>{plat}</option>
                    ))}
                </select>
                {(filterCategory || filterPlatform) && (
                    <button
                        onClick={() => {
                            setFilterCategory("");
                            setFilterPlatform("");
                        }}
                        className="text-red-500 text-sm hover:underline"
                    >
                        Clear Filters
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {isLoading ? (
                    <div className="p-12 flex justify-center">
                        <Loader />
                    </div>
                ) : error ? (
                    <div className="p-12 text-center text-red-500">
                        Failed to load data. Please try again.
                    </div>
                ) : links.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        No links found. Create your first one!
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-semibold">
                                <tr>
                                    <th className="px-6 py-4">Title / URL</th>
                                    <th className="px-6 py-4">Category</th>
                                    <th className="px-6 py-4">Platform</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {links.map((link) => (
                                    <tr key={link._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-medium text-gray-900 mb-1">
                                                    {link.title || "Untitled"}
                                                </span>
                                                <a
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-[--primary] text-xs hover:underline flex items-center gap-1 max-w-[200px] truncate"
                                                >
                                                    <FiExternalLink className="w-3 h-3" />
                                                    {link.url}
                                                </a>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium border border-blue-100">
                                                {link.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs font-medium border border-purple-100">
                                                {link.platform}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${link.isActive
                                                    ? "bg-green-100 text-green-800"
                                                    : "bg-gray-100 text-gray-800"
                                                    }`}
                                            >
                                                {link.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(link)}
                                                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-[--primary] transition-colors"
                                                >
                                                    <FiEdit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(link._id)}
                                                    className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-500 transition-colors"
                                                >
                                                    {deleteMutation.isPending && deleteMutation.variables === link._id ? (
                                                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                                                    ) : (
                                                        <FiTrash2 className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">
                                {editingLink ? "Edit Link" : "Add New Link"}
                            </h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <span className="text-2xl">×</span>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.category}
                                    onChange={(e) =>
                                        setFormData({ ...formData, category: e.target.value })
                                    }
                                    placeholder="e.g., Day in the Life"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none transition-all"
                                    list="categories-list"
                                />
                                <datalist id="categories-list">
                                    {uniqueCategories.map(cat => (
                                        <option key={cat} value={cat} />
                                    ))}
                                </datalist>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Platform <span className="text-red-500">*</span>
                                </label>
                                <select
                                    required
                                    value={formData.platform}
                                    onChange={(e) =>
                                        setFormData({ ...formData, platform: e.target.value })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none transition-all"
                                >
                                    <option value="">Select Platform</option>
                                    <option value="TikTok">TikTok</option>
                                    <option value="Instagram">Instagram</option>
                                    <option value="Facebook">Facebook</option>
                                    <option value="YouTube">YouTube</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    URL <span className="text-red-500">*</span>
                                </label>
                                <div className="flex">
                                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                        <FiLink />
                                    </span>
                                    <input
                                        type="url"
                                        required
                                        value={formData.url}
                                        onChange={(e) =>
                                            setFormData({ ...formData, url: e.target.value })
                                        }
                                        placeholder="https://..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Title (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) =>
                                        setFormData({ ...formData, title: e.target.value })
                                    }
                                    placeholder="Short description..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none transition-all"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={formData.isActive}
                                    onChange={(e) =>
                                        setFormData({ ...formData, isActive: e.target.checked })
                                    }
                                    className="w-4 h-4 text-[--primary] border-gray-300 rounded focus:ring-[--primary]"
                                />
                                <label htmlFor="isActive" className="text-sm text-gray-700">
                                    Active
                                </label>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isPending || updateMutation.isPending}
                                    className="flex-1 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                                >
                                    {(createMutation.isPending || updateMutation.isPending) && (
                                        <ButtonLoader customClass="w-4 h-4 border-2 border-white border-t-transparent" />
                                    )}
                                    {editingLink ? "Update Link" : "Create Link"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Wrapper>
    );
};

export default AIFeeding;
